import { SyncService } from "frontend/services/syncService";
import {
  useCanvasElementsObjects,
  useCanvasMetadata,
  useClientState,
  useCollaboratorIDs,
  useIntegrationTasks,
  useSortedFrames,
} from "frontend/subscriptions";
import { CursorType, getCursorTypeForElementType, isCursorTypeLimited } from "./cursor-type";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Reflect } from "@workcanvas/reflect/client";
import { M } from "shared/datamodel/mutators";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import AppHeader from "frontend/app-header/app-header";
import style from "./index.module.css";
import CanvasSideMenu from "./sidebar/side-menu";
import { PlanFeatureName, TipName } from "shared/datamodel/schemas";
import { Point } from "shared/datamodel/schemas/canvas-element";
import CanvasStage from "./canvas-stage";
import { ElementDrawProps, NewElementData } from "shared/datamodel/canvas-element";
import { dbKey, isElementNumberLimited } from "shared/util/utils";
import consts, { MIN_PAID_SEATS, Plan } from "shared/consts";
import { Collaborator } from "frontend/collaborator";
import { useBoardValue } from "frontend/state/board-state";
import BoardUsersPresence from "frontend/user-presence/board-users-presence";
import useStateValue from "frontend/state/value";
import { pdfCacheService } from "frontend/services/pdf-cache-service";
import { pdfPageCacheService } from "frontend/services/pdf-page-cache-service";
import tracking from "frontend/tracking";
import CanvasElementsSchemaViewer from "frontend/canvas-controls/dev-tools/element-schema-viewer";
import ErrorToast from "frontend/ui-components/error-toast";
import Modal from "frontend/modal/modal";
import classNames from "classnames";
import CanvasSidePanel from "./side-panel/side-panel";
import AppLoader from "frontend/loader/app-loader";
import CanvasPresentationView from "./presentation-mode/canvas-presentation-view";
import { Footer } from "../canvas-controls/footer";
import { ZoomControls } from "../canvas-controls/zoom-controls/zoom-controls";
import { UndoRedoControls } from "../canvas-controls/undo-redo-controls";
import { ToggleSideMenu } from "../canvas-controls/toggle-side-menu";
import { ExportConfiguration, ExportDialog } from "frontend/canvas-controls/export-canvas/export-dialog";
import { ExportButton } from "frontend/canvas-controls/export-canvas/export-button";
import { useRouter } from "next/router";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  posScaleAtom,
  selectedElementIdsAtom,
  mondayIntegrationPopupAtom,
  undoRedoStackAtom,
  submenuDataAtom,
} from "state-atoms/stage-atoms";
import {
  stageRefAtom,
  createIntegrationTypeAtom,
  invoicePreviewsAtom,
  SidepanelType,
  sidePanelType,
  syncServiceAtom,
  shouldFitWhenPlacingElementRefAtom,
} from "state-atoms";
import {
  errorMessageAtom,
  HtmlDialogDisplay,
  htmlDisplayAtom,
  isBannerNotificationOnAtom,
  isUserDisconnectedAtom,
  layerRefAtom,
  showRestrictedModalAtom,
} from "state-atoms/general-atoms";
import CreateTemplateModal from "frontend/modals/create-template-modal";
import TemplateLinkModal from "frontend/modals/template-link-modal";
import { accountAtom, userTipsAtom } from "state-atoms/users-atoms";
import { useCord, NoCordMsg, cordEnabled } from "frontend/hooks/use-cord";
import { CordProvider } from "@cord-sdk/react";
import { PortalID } from "frontend/modal/portal-types";
import EditSeatsModal from "frontend/modals/edit-seats-modal";
import { CanvasTemplate } from "shared/datamodel/schemas/canvas-template";
import { VotingButton } from "frontend/canvas-controls/voting-button";
import { VotingMainPanel } from "./voting/panel/voting-main-panel";
import FollowingCollaboratorContainer from "./following-collaborator-container";
import { VotingSessionCountdown } from "./voting/voting-countdown";
import { useUpdateOnChange } from "frontend/hooks/use-update-on-change";
import Checkout, { CheckoutSource, CheckoutSources } from "frontend/billing/checkout";
import { Timer } from "frontend/canvas-designer-new/timer/timer";
import { markTipSeen, retrieveUpcomingInvoicePreview } from "frontend/api";
import { useTemplateAutoPlacement } from "frontend/hooks/place-template-hooks";
import { isBasicPlan, planToString } from "shared/util/billing";
import { stringToPlan } from "frontend/billingUtils";
import MondayIntegrationSidePanel from "./integrations/side-panel/monday-integration-side-panel";
import { placeIntegrationElement } from "shared/datamodel/integration-item";
import CanvasMindmapOrgChartSidePanel from "./elements/org-chart/mindmap-org-chart-side-panel";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { VideoCallUi } from "./video-calls/video-call-ui";
import { VideoCallButton } from "frontend/canvas-controls/video/video-call-button";
import OrgChartSidePanel from "./side-panel/orgchart-side-panel";
import type { Patch as ImmerPatch } from "immer";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import {
  countOrgChartNodesInReflect,
  useOrgChartPremiumFeatures,
} from "frontend/canvas-designer-new/elements/org-chart/orgchart-utils";
import ModalManager, { ModalAction } from "frontend/modals/modal-manager";
import { RESET } from "jotai/utils";
import useModalDeepLink from "frontend/hooks/use-modal-deep-link";
import { count } from "rambda";
import ShareCanvasModalNew from "frontend/modals/share-canvas/share-canvas-modal-new";
import VideoPlayerModal from "frontend/modals/video-player-modal";
import useAbility from "frontend/hooks/use-ability";
import { InstanceType } from "shared/datamodel/schemas/ability";
import RestrictedUserModalNew from "frontend/modals/restricted-user-new-modal";
import useUsers from "frontend/hooks/use-users";
import { useTeam } from "frontend/hooks/use-team";
import RestrictedUserNoticeModalNew from "frontend/modals/restricted-user-notice-new-modal";
import { ReadTransaction } from "@workcanvas/reflect";
import { DevToolsButton } from "frontend/canvas-controls/dev-tools/dev-tools-button";
import { useCanvasSettings } from "frontend/hooks/use-canvas-settings";
import { walkthroughTips } from "frontend/walkthrough-tips/walkthrough-tip-model";
import useStats from "frontend/hooks/performance/use-stats";
import { FPSMeterButton } from "frontend/canvas-controls/dev-tools/fps-meter-button";
import { KonvaPerfButton } from "frontend/canvas-controls/dev-tools/konva-performace-metrics-button";
import { MondayIntegrationPopup } from "frontend/canvas-designer-new/integrations/monday-integration-popup/monday-integration-popup";
import { useActiveVotingSession } from "./voting/voting-common";
import { VotingUploader } from "./voting/voting-upload-results";
import { UploadInProgress } from "./voting/panel/components/uploading-msg";
import {
  findKonvaNodeByAttribute,
  getAllNodes,
  getContentsOfElementsLayer,
  findKonvaNodesByType,
} from "utils/canvas-test-utils";
import ShapesSidePanel from "./shape-libraries/side-panel/shapes-side-panel";
import { ShapeInfo } from "./shape-libraries/side-panel/virtualized-list/virtualized-row";
import BoardTitle from "./board-title/board-title";
import SignUpToContinue from "frontend/modals/upgrade/sign-up-to-continue";
import { signUpToContinueModalAtom } from "state-atoms/modal-atoms";
import { isVideoCallUiOpenAtom } from "frontend/canvas-controls/video/video-atom";
import { NounIconPanelContainer, nounIconPanelState } from "./submenus/noun-icons-submenu";
import { SubmenuData, SubmenuDataUpdate } from "./canvas-submenu";
import useElementsLimitPerPlanFeature from "frontend/hooks/use-elements-limit";
import UpgradeCTA from "frontend/ui-components/upgrade-cta/upgrade-cta";
import { CommentButton } from "frontend/canvas-controls/comment";
import { isCIM, useIsCIMReorderEnabled } from "utils/url-utils";
import { waitForFitToNewElements, animateToFitStage } from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { PosScale } from "frontend/konva-stage-types";
import { countCanvasStageElements, countCanvasStageElementsPerType } from "utils/canvas-utils";
import { AIGeneratorPanel } from "./ai-generator/ai-generator-panel";
import { MondayItemManagerProvider } from "./integrations/components/monday-item-manager-context";
import { useUndoRedoControls } from "frontend/hooks/undo-redo/undo-redo.context";
import { HeaderProvider } from "frontend/context/header-context";
import { useApplyViewerRestricted } from "frontend/hooks/use-apply-viewer-restricted";
import { AppFooter } from "frontend/app-footer/app-footer";
import { FooterProvider } from "frontend/context/footer-context";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { clientStatePrefix } from "shared/datamodel/client-state";
import type { PointInViewport } from "frontend/utils/math-utils";
import useCheckoutFlow from "frontend/billing/use-checkout-flow";
import CIMOnboardingFlow from "frontend/canvas-onboarding/cim-onboarding/cim-onboarding-flow";
import CIMContentCards from "frontend/ui-components/canvas-content-card/cim/cim-content-cards";
import { placeMondayTask } from "shared/datamodel/monday-task";
import { MondayTaskElement } from "elements/monday-task/schema";
import { IntegrationItem } from "shared/datamodel/schemas/integration-item";

export interface SinglePatch {
  id: string;
  patch: ImmerPatch[];
  inversePatch: ImmerPatch[];
  details?: any;
}

export const boundsRefreshOperation = {
  undo: "undo",
  redo: "redo",
  copyPaste: "copyPaste",
  lockUnlock: "lockUnlock",
  group: "group",
  ungroup: "ungroup",
  selectNewElements: "selectNewElements",
  moveElements: "moveElements",
};

export type PatchAnything = (changes: SinglePatch | ReadonlyArray<SinglePatch>, noUndo?: boolean) => void;

const OLD_SHAPE_TYPES = Object.values(consts.SHAPES);

export default function CanvasDesigner({
  reps,
  customColors,
  isReadOnly,
  showDevTools: initialShowDevTools = false || window.location.hostname === "localhost",
  isIframe = false,
  isOnline = false,
}: {
  reps: Reflect<M>;
  customColors: string[];
  isReadOnly: boolean;
  showDevTools?: boolean;
  isIframe?: boolean;
  isOnline?: boolean;
}) {
  //Atoms - set
  const userTips = useAtomValue(userTipsAtom);
  const syncService = useAtomValue(syncServiceAtom);
  const [posScaleAtomValue, setPosScaleAtom] = useAtom(posScaleAtom);
  const [submenuData, setSubmenuData] = useAtom(submenuDataAtom);
  const account = useAtomValue(accountAtom);
  const [htmlDisplay, setHtmlDisplay] = useAtom(htmlDisplayAtom);
  const setInvoicePreviewsAtom = useSetAtom(invoicePreviewsAtom);
  const isBannerNotificationOn = useAtomValue(isBannerNotificationOnAtom);
  const isUserDisconnected = useAtomValue(isUserDisconnectedAtom);
  const shouldShowMondayIntegrationPopup = useAtomValue(mondayIntegrationPopupAtom);
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const stageRefAtomValue = useAtomValue(stageRefAtom);

  const setShouldFitWhenPlacingElementRef = useSetAtom(shouldFitWhenPlacingElementRefAtom);
  const shouldFitWhenPlacingElementRef = useRef(false);

  useEffect(() => {
    setShouldFitWhenPlacingElementRef(shouldFitWhenPlacingElementRef);
  }, [setShouldFitWhenPlacingElementRef]);

  useEffect(() => {
    tracking.trackAnalyticsEvent("canvas_designer_mounted");
  }, []);

  const {
    executeMutation,
    executeUndo,
    executeRedo,
    patchElementsWithUndoRedo,
    canUndo,
    canRedo,
    needToRefreshBoundsRef,
  } = useUndoRedoControls();

  // Board state
  const [{ config: boardConfig, board, user, stageRef, pusherChannel }, boardValueDispatch] = useBoardValue();
  const [globalState] = useStateValue();
  const createIntegrationType = useAtomValue(createIntegrationTypeAtom);

  // State
  const [cursorType, setCursorType] = useState(CursorType.pointer);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [nounOpen, setNounOpen] = useAtom(nounIconPanelState);
  const [showSideMenuOverflow, setShowSideMenuOverflow] = useState(false);
  const [selectedElementsId, setSelectedElementsIds] = useAtom(selectedElementIdsAtom);
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);

  useEffect(() => {
    return () => {
      // Clear PDF caches when board unmounts
      pdfCacheService.clearAll();
      pdfPageCacheService.clearAll();
    };
  }, []);
  const [votingPanelOpen, setVotingPanelOpen] = useState(false);
  const [videoPanelOpen, setVideoPanelOpen] = useAtom(isVideoCallUiOpenAtom);
  const [enableComments, setEnableComments] = useState<boolean | undefined>(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRestrictedNoticedModal, setShowRestrictedNoticeModal] = useState<boolean>(false);
  const [showDevTools, setShowDevTools] = useState(false);
  const [showFpsMeter, setShowFpsMeter] = useState(false);
  const [showKonvaPerformanceMetrics, setShowKonvaPerformanceMetrics] = useState(false);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const isCimReorderImportMondayItemsEnabled = useFeatureFlag("cim-reorder-import-monday-items");

  const newMondayTaskEnabled = useFeatureFlag("new-monday-task-element");

  const { sortedFrames, hiddenFrameIds } = useSortedFrames(syncService?.getReplicache());
  const visibleFrames = sortedFrames.filter((f) => !hiddenFrameIds.includes(`frame-${f.id}`));

  const [passwordChecked, setPasswordChecked] = useState<boolean>(board?.isPasswordProtected ?? false);
  const [showExploreOrgChart, setShowExploreOrgChart] = useState(false);

  const showLeftSidePanel = !isCimReorderEnabled && openedSidePanel == SidepanelType.framesAndComments;
  const showIntegrationsSidePanel = openedSidePanel == SidepanelType.integrations;
  const showShapesSidePanel = openedSidePanel == SidepanelType.shapes;
  const showOrgChartNodeInfo = openedSidePanel == SidepanelType.mondayOrgChartNodeInfo;

  const integrationElements = useIntegrationTasks(reps);
  const integrationElementsKey = JSON.stringify(integrationElements);

  const { addItemsToQueue } = useBoardTasks(board?.documentId);

  const videoCallsFeature = usePlanFeatureValue(PlanFeatureName.VIDEO_CHAT);
  const { canAddElements: canAddElementsWithMondayLimitation } = useElementsLimitPerPlanFeature(
    PlanFeatureName.MONDAY_INTEGRATION_LIMIT
  );
  const applyRestrictedLimitation = useApplyViewerRestricted();
  const { getDefaultAccountTeam } = useTeam();

  const { canAddOrgChartFields, maxOrgChartNodes } = useOrgChartPremiumFeatures();

  const isSidePanelHidden =
    !(
      showLeftSidePanel ||
      showIntegrationsSidePanel ||
      showShapesSidePanel ||
      showOrgChartNodeInfo ||
      openedSidePanel == SidepanelType.orgChartNodeInfo ||
      showIntegrationsSidePanel
    ) || isUserDisconnected;
  const canvasElementsLimit = usePlanFeatureValue(PlanFeatureName.CANVAS_ELEMENTS_LIMIT);
  const restrictedFreeElementsNumber = user?.isAnonymous ? false : canvasElementsLimit;

  const isShapePanelUnlocked = usePlanFeatureValue(PlanFeatureName.PREMIUM_SHAPES);

  useStats(showFpsMeter);

  useEffect(() => {
    if (isCIM()) {
      return;
    }
    if (createIntegrationType) {
      setOpenedSidePanel(SidepanelType.integrations);
    }
  }, [createIntegrationType]);

  useEffect(() => {
    if (board?.documentId && Object.keys(integrationElements).length > 0) {
      addItemsToQueue(integrationElements);
    }
  }, [board?.documentId, integrationElementsKey]);

  useEffect(() => {
    if (applyRestrictedLimitation && !userTips.has(TipName.restrictedUsersNotice) && isRestrictedUser) {
      setShowRestrictedNoticeModal(true);
    }
  }, [userTips]);

  const [error, setError] = useAtom(errorMessageAtom);
  const layerRef = useAtomValue(layerRefAtom);
  // const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useCheckoutFlow();
  const setShowSignUpToContinueModal = useSetAtom(signUpToContinueModalAtom);
  const [isPresentation, setIsPresentation] = useState(false);
  const [isCreatingTemplate, setCreatingTemplate] = useState(false);
  const [showTemplateLinkModal, setShowTemplateLinkModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useAtom(showRestrictedModalAtom);
  const [showUploadingVotesModal, setShowUploadingVotesModal] = useState(false);

  const setErrorCallback = useCallback(setError, [setError]);
  const closeVideoPanelCallback = useCallback(() => setVideoPanelOpen(false), [setVideoPanelOpen]);

  // Canvas
  const collaboratorsCursorIds = useCollaboratorIDs(reps, false);
  const collaboratorsIds = useCollaboratorIDs(reps, true);
  const [followingCollaboratorId, setFollowingCollaboratorId] = useState<string | null>(null);
  const followingCollaborator = useClientState(reps, followingCollaboratorId);
  const { canPerformAnyAction, canEditInstance, canInviteToInstance } = useAbility();
  const isRestrictedUser = board && !canPerformAnyAction(globalState?.user ?? null, board, InstanceType.Board);
  const canEditBoard =
    board && canEditInstance(globalState?.user ?? null, board, InstanceType.Board) && !isUserDisconnected;

  const { getSetting } = useCanvasSettings(syncService);
  const showCursors = getSetting("showCursors");

  // if following collaborator is not in collaboratorsIds, set it to null
  if (followingCollaboratorId && !collaboratorsIds.includes(followingCollaboratorId)) {
    setFollowingCollaboratorId(null);
  }
  const setFollow = useCallback(
    (id) => {
      setFollowingCollaboratorId((oldId) => {
        if (oldId === id) {
          tracking.trackEvent(consts.TRACKING_CATEGORY.COLLABORATION, "stop-following-collaborator");
          return null;
        }
        tracking.trackEvent(consts.TRACKING_CATEGORY.COLLABORATION, "start-following-collaborator");
        return id;
      });
    },
    [setFollowingCollaboratorId]
  );

  const posScale = followingCollaborator?.scale
    ? {
        scale: followingCollaborator.scale,
        x: followingCollaborator.position!.x,
        y: followingCollaborator.position!.y,
      }
    : posScaleAtomValue;

  if (
    posScale.scale != posScaleAtomValue.scale ||
    posScale.x != posScaleAtomValue.x ||
    posScale.y != posScaleAtomValue.y
  ) {
    setPosScaleAtom(posScale);
  }

  const handleSetVotingOpen = useCallback(
    (open: boolean) => {
      setVotingPanelOpen(open);
      if (open) {
        setNounOpen(false);
        if (cursorType === CursorType.nounIcon) {
          setCursorType(CursorType.pointer);
        }
      }
    },
    [cursorType, setNounOpen]
  );

  const currentVotingSession = useActiveVotingSession(syncService);
  const changed = useUpdateOnChange(currentVotingSession?.start);
  if (changed && !votingPanelOpen) {
    handleSetVotingOpen(true);
  }

  // Refs
  const stageDivRef = useRef<any>(null);
  const timerButtonRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const cordAuth = useCord({ documentId: board?.documentId, isLoggedIn: !!user && !user.isAnonymous });

  /* Test data */
  const testAccounts = [
    5,
    14288737,
    16526228,
    15458881,
    16446498,
    18094136,
    18094148,
    18094156,
    18094163,
    18097744,
    18132019,
    32831744,
    18617988, // 18617988 testybesty
  ].map(String); // monday, testybesty
  const isTestSession =
    process.env.NEXT_ENV !== "production" ||
    (process.env.NEXT_ENV === "production" && account && testAccounts.includes(account.id));
  const boardTestData = useCanvasElementsObjects(isTestSession ? syncService?.getReplicache() : null);
  // TODO: performance fix this double call to useCanvasElementsObjects
  // const boardTestData = null;
  const boardTestMetadata = useCanvasMetadata(isTestSession ? syncService?.getReplicache() : null);
  const posScaleRef = useRef<PosScale>(posScale);
  posScaleRef.current = posScale;

  const { setModalDeepLink } = useModalDeepLink();

  const instance = board?.projectDocumentId
    ? { type: InstanceType.Project, id: board.projectDocumentId }
    : { type: InstanceType.Team, id: board?.teamDocumentId ?? getDefaultAccountTeam()?.documentId };

  const { users, loadUsers } = useUsers(instance.type, instance.id);

  async function loadUsersIfNeeded() {
    try {
      if (users?.length === 0) {
        await loadUsers();
      }
    } catch (error) {
      console.error("An error occurred while loading users:", error);
    }
  }

  useEffect(() => {
    loadUsersIfNeeded();
  }, [users]);

  useEffect(() => {
    if (isTestSession) {
      window.Board.data = boardTestData;
      window.Board.metadata = boardTestMetadata;
      window.Board.onAddElements = onAddElements;
      window.Board.canvasToScreen = (pos: Point) => {
        const x = pos.x * posScaleRef.current.scale + posScaleRef.current.x;
        const y = pos.y * posScaleRef.current.scale + posScaleRef.current.y;
        return { x, y };
      };
      window.Board.getSelectedIds = () => selectedElementsId;

      window.Board.getAllNodes = getAllNodes;
      window.Board.getContentsOfElementsLayer = getContentsOfElementsLayer;
      window.Board.findKonvaNodeByAttribute = findKonvaNodeByAttribute;
      window.Board.findKonvaNodesByType = findKonvaNodesByType;
      window.Board.deleteAllElements = () => {
        syncService?.mutate.removeMany({
          ids: boardTestData.elements.map((e) => `${e.type}-${e.id}`) ?? [],
        });
      };
      window.Board.getClientsState = (showStale = false) =>
        syncService?.getReplicache()?.query(async (tx) => {
          const clientEntries = await tx.scan({ prefix: clientStatePrefix }).entries().toArray();
          if (!showStale) {
            return clientEntries.filter(
              (entry) => entry[1] && typeof entry[1] === "object" && (entry[1] as any).present
            );
          }
          return clientEntries;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardTestData, isTestSession, onAddElements]);
  /* End test data */

  useEffect(() => {
    const isPresenting = router.query.presentation === "true" || false;
    setIsPresentation(isPresenting);
  }, [router.query.presentation]);

  useEffect(() => {
    if (isCimReorderEnabled) {
      if (cursorType == CursorType.nounIcon) {
        setOpenedSidePanel(SidepanelType.nounIcon);
      } else if (openedSidePanel === SidepanelType.nounIcon) {
        setOpenedSidePanel(SidepanelType.none);
      }
    } else {
      setNounOpen(cursorType == CursorType.nounIcon);
    }
  }, [cursorType]);

  // when loading a new board (just created) we...
  //   open template modal for blank boards
  //   open integration-monday window for mindmap-orgchart
  //   focus the orgchart node for orgchart-2
  useEffect(() => {
    if (!board) return;
    // if we're coming back after integrating with monday-account for org chart, pop up the template modal
    const showOrgChartTemplatesModal = sessionStorage.getItem(
      consts.SESSION_STORAGE_KEYS.SHOW_ORG_CHART_TEMPLATES_ON_START
    );
    if (showOrgChartTemplatesModal && showOrgChartTemplatesModal === "true") {
      sessionStorage.removeItem(consts.SESSION_STORAGE_KEYS.SHOW_ORG_CHART_TEMPLATES_ON_START);
      setCursorType(CursorType.mindmapOrgChart);
      setShowSubmenu(true);
    }

    const firstAccessToBoard = sessionStorage.getItem(consts.SESSION_STORAGE_KEYS.FIRST_ACCESS);
    if (firstAccessToBoard == "true") {
      sessionStorage.removeItem(consts.SESSION_STORAGE_KEYS.FIRST_ACCESS);
      if (board.createdFromTemplateId) {
        const id = board.createdFromTemplateId;
        if (process.env.MONDAY_ORGCHART_TEMPLATE_ID && id.match(process.env.MONDAY_ORGCHART_TEMPLATE_ID)) {
          setCursorType(CursorType.mindmapOrgChart);
          setShowSubmenu(true);
        }
      }
    }
  }, [board?.id, board?.createdFromTemplateId]);

  useEffect(() => {
    if (!followingCollaborator) {
      setFollowingCollaboratorId(null);
    }
  }, [followingCollaborator]);

  //Hide the error message after some interval
  useEffect(() => {
    let timeOut: any;
    if (error) {
      timeOut = setTimeout(() => {
        setError(null);
      }, 6000);
    }
    return () => timeOut && clearTimeout(timeOut);
  }, [error]);

  useEffect(() => {
    // The comments enabled will be delayed by 2 seconds so they can handle the wait time for the websocket to be established.
    setTimeout(() => {
      setEnableComments(!!cordAuth || user?.isAnonymous);
    }, 2000);
  }, [cordAuth, user?.isAnonymous]);

  useEffect(() => {
    async function previewInvoices() {
      const seatsCount = globalState.user?.planInfo?.seats_count;
      if (seatsCount) {
        const paidPlans = Object.values(Plan)
          .filter((index) => isNaN(Number.parseInt(index as string)))
          .filter((index) => index != planToString(Plan.basic)?.toLowerCase());
        const invoicePromises: Promise<any>[] = [];
        for (const plan of paidPlans) {
          for (const period of ["month", "year"]) {
            const invoicePromise = retrieveUpcomingInvoicePreview(
              stringToPlan(plan as string)!,
              Math.max(seatsCount, MIN_PAID_SEATS),
              period as any
            );
            invoicePromises.push(invoicePromise);
          }
        }
        const invoices = await Promise.all(invoicePromises);
        console.log(`index previewInvoices for ${seatsCount} seats`, invoices);
        setInvoicePreviewsAtom(invoices);
      }
    }
    if (isBasicPlan(globalState?.user?.planInfo?.plan_id)) {
      console.log("Calling index previewInvoices", globalState.user?.planInfo?.seats_count);
      previewInvoices();
    }
  }, [globalState.user?.planInfo?.seats_count]);

  function handleModalAction(action: ModalAction) {
    if (action) {
      switch (action.type) {
        case "chooseTemplate": {
          onChooseTemplate(action.payload);
          break;
        }
        case "dismissTemplate": {
          setCursorType(CursorType.pointer);
          break;
        }
      }
    }
  }

  function onChangePosScale(posScale: { scale: number; x: number; y: number }) {
    setPosScaleAtom(posScale);
    updateClientViewport(posScale);
  }

  useEffect(() => {
    setSubmenuData((previous: any) => ({ ...previous, nounIcon: null }));
  }, [nounOpen, isCimReorderEnabled, openedSidePanel]);

  async function submenuItemClicked(item: CursorType) {
    let newSubMenuState = true;
    // HACK: noun icons panel looks like submenu, but it's opened and closed differently.
    // it's not closed after placing an icon, it's either closed by the (x) button inside it,
    // or by switching to a new tool.
    // Also, if we switched to noun, close other submenus;
    if (
      item == CursorType.nounIcon ||
      cursorType == CursorType.nounIcon ||
      cursorType == CursorType.cimWidgets ||
      (cursorType == CursorType.mondayIntegration && isCimReorderImportMondayItemsEnabled)
    ) {
      if (isCimReorderEnabled) {
        if (item == CursorType.nounIcon) {
          setOpenedSidePanel((previous) =>
            previous === SidepanelType.nounIcon ? SidepanelType.none : SidepanelType.nounIcon
          );
        } else if (openedSidePanel === SidepanelType.nounIcon) {
          setOpenedSidePanel(SidepanelType.none);
        }
      } else {
        setNounOpen((p) => {
          if (!p) {
            setVotingPanelOpen(false);
          }
          return !p;
        });
      }
    }
    if (item == CursorType.nounIcon) {
      newSubMenuState = false;
    }

    // Clicking on the current tool -> close it and revert to default
    // I define "easy-access" tools that clicking on them should reopen the submenu (if it's not open)
    // instead of reverting to pointer. If the submenu is open, then we deselect the yool
    const easyAccessTools = item === CursorType.draw || item === CursorType.stickyNote || item === CursorType.shape;

    if (item == cursorType && !easyAccessTools) {
      item = CursorType.pointer;
    }

    if (item === cursorType && easyAccessTools && showSubmenu) {
      item = CursorType.pointer;
      newSubMenuState = false;
    }

    if (item == CursorType.comment) {
      const visitorFromAnotherAccount = board?.accountId != globalState.account?.id;
      if (visitorFromAnotherAccount) {
        setError(NoCordMsg);
        return;
      }
    }
    setShowSideMenuOverflow(item === CursorType.sideMenuOverflow);

    //let's check if user has element limit, and if he is about to exceed it
    // this way we can popup a notification before he tries to place an element
    if (restrictedFreeElementsNumber && isCursorTypeLimited[item]) {
      const currentElementCount = countCanvasStageElements(stageRef);
      if (currentElementCount >= restrictedFreeElementsNumber) {
        showBlockedActionModal("elements-limit");
        return;
      }
    }
    setCursorType(item);
    setShowSubmenu(newSubMenuState);
  }

  function showBlockedActionModal(reason: CheckoutSource["name"]) {
    if (user?.isAnonymous) {
      setShowSignUpToContinueModal(true);
    } else {
      setShowUpgradeModal({ name: reason, value: "" });
    }
  }

  async function onAddElements(
    elements: NewElementData[],
    addUndo: boolean = true,
    source: string = "unknown",
    sequenceId?: string
  ): Promise<string[]> {
    let countPerElementType: Record<string, any> = {};
    if (restrictedFreeElementsNumber) {
      // check if we're adding elements whose number should be limited
      const limitedElements = count((x) => isElementNumberLimited(x.element), elements);
      const currentCount = countCanvasStageElements(stageRef);
      countPerElementType = countCanvasStageElementsPerType(stageRef);
      tracking.trackAnalyticsEvent("new_item_placed_basic_plan", {
        elementsAdded: elements.length,
        boardId: board?.id,
        numberOfElementsInBoard: currentCount + limitedElements,
        source,

        // Legacy values
        category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
        stringValue: board?.id,
        property: currentCount + limitedElements,
      });
      if (currentCount + limitedElements > restrictedFreeElementsNumber) {
        showBlockedActionModal("elements-limit");
        return [];
      }
    }

    if (elements.some((element) => element.type != consts.CANVAS_ELEMENTS.DRAWING)) {
      // Do not track drawing event as it is currently considered low-quality signal
      tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.NEW_ITEM_PLACED);
    }

    for (const element of elements) {
      let shapeSubtype = element.element.subtype;
      if (OLD_SHAPE_TYPES.includes(element.element.type) && !shapeSubtype) {
        shapeSubtype = element.element.type;
      }

      // If the user has a limit on the number of elements, we track the total count of elements of this type
      countPerElementType[element.type] ??= 0;
      const totalCountOfType = restrictedFreeElementsNumber ? ++countPerElementType[element.type] : undefined;

      tracking.trackAnalyticsEvent("new_element_placed", {
        elementType: element.type,
        shapeSubtype,
        boardId: board?.id,
        source,
        typeCount: totalCountOfType,

        // Legacy values
        category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
        stringValue: element.type,
        property: board?.accountId,
        floatValue: user?.id,
      });
    }

    const newIds = elements.map((element) => dbKey(element.id, element.type));

    // if we're adding undo, we'll add the elements as hidden first so the undo action will return
    // it to being hidden
    const withHidden = elements.map((element) => ({ ...element, element: { ...element.element, hidden: true } }));
    await syncService?.mutate.putElements({ ids: newIds, elements: addUndo ? withHidden : elements });
    if (addUndo) {
      await patchElementsWithUndoRedo?.(
        newIds,
        (element) => {
          element.hidden = false;
        },
        sequenceId
      );
    }

    if (!shouldFitWhenPlacingElementRef.current && layerRef?.current && stageRefAtomValue?.current) {
      waitForFitToNewElements(new Set(newIds), layerRef.current, stageRefAtomValue.current, posScaleRef.current)
        .then((targetViewport: PosScale | null) => {
          if (!targetViewport) return;
          if (stageRefAtomValue.current) {
            animateToFitStage(stageRefAtomValue.current, targetViewport, onChangePosScale);
          }
        })
        .catch(() => {});
    }

    return newIds;
  }

  async function onDeleteElements(ids: string[], addUndo: boolean = true, sequenceId?: string) {
    if (ids && ids.length > 0 && ids[0]) {
      if (addUndo) {
        // Support sequenceId for delete operations
        if (sequenceId) {
          await patchElementsWithUndoRedo?.(
            ids,
            (element) => {
              element.hidden = true;
            },
            sequenceId
          );
        } else {
          // Use old method for non-sequenceId delete operations
          executeMutation?.({
            do: () => executeSetElementsHidden(ids, true),
            undo: () => executeSetElementsHidden(ids, false),
          });
        }
      } else {
        executeSetElementsHidden(ids, true);
      }
    }
  }

  function onReplaceElements(newIds: string[], oldIds: string[]) {
    if (newIds && oldIds) {
      executeMutation?.({
        do: () => executeReplaceElements(newIds, oldIds),
        undo: () => executeReplaceElements(oldIds, newIds),
      });
    }
  }

  function onChangeElement(
    id: string,
    props: Map<string, any>,
    previousProps: Map<string, any>,
    addUndo: boolean,
    updateSubMenuData?: any
  ) {
    if (addUndo) {
      executeMutation?.({
        do: () => {
          executeChangeElement(id, props, previousProps);
        },
        undo: () => {
          executeChangeElement(id, previousProps, props);
        },
      });
    } else {
      executeChangeElement(id, props, previousProps);
    }
    if (updateSubMenuData) {
      setSubmenuData((previous) => ({ ...previous, ...updateSubMenuData }));
    }
  }

  function _patchCanvasElement(id: string, patch: ImmerPatch[]) {
    syncService?.mutate.patchCanvasEl({ changes: [{ id, patch }] });
  }

  const patchCanvasElement = useCallback(
    ({
      id,
      patch,
      inversePatch,
      details,
    }: {
      id: string;
      patch: ImmerPatch[];
      inversePatch?: ImmerPatch[];
      details?: any;
    }) => {
      // guard against empty patch; we don't want to create empty undo entry
      // This should never happen in theory, but in practice it does
      if (patch.length === 0) {
        return;
      }
      if (details?.action == "orgchart-add-node") {
        const numberNodes = details.count ?? 1;
        countOrgChartNodesInReflect(syncService)?.then((total: any) => {
          if (total + numberNodes > maxOrgChartNodes) {
            showBlockedActionModal("org-chart-nodes");
          } else if (inversePatch) {
            executeMutation?.({
              do: () => _patchCanvasElement(id, patch),
              undo: () => _patchCanvasElement(id, inversePatch),
            });
          } else {
            _patchCanvasElement(id, patch);
          }
        });
      } else if (inversePatch) {
        executeMutation?.({
          do: () => _patchCanvasElement(id, patch),
          undo: () => _patchCanvasElement(id, inversePatch),
        });
      } else {
        _patchCanvasElement(id, patch);
      }
    },
    [user, globalState.user, syncService]
  );

  function _patchAnything(id: string, patch: ImmerPatch[]) {
    syncService?.mutate.patchAnything({ changes: [{ id, patch }] });
  }

  function isSinglePatch(patch: SinglePatch | ReadonlyArray<SinglePatch>): patch is SinglePatch {
    return !Array.isArray(patch);
  }
  const patchAnything = useCallback<PatchAnything>(
    (changes, noUndo = false) => {
      //make sure _patchAnything calls syncService.mutate.patchAnything(changes);
      if (isSinglePatch(changes)) {
        //ariel
        if (changes.patch.length === 0) {
          return;
        }
        if (changes.inversePatch) {
          executeMutation?.({
            do: () => _patchAnything(changes.id, changes.patch),
            undo: () => _patchAnything(changes.id, changes.inversePatch),
          });
        } else {
          _patchAnything(changes.id, changes.patch);
        }
      } else if (changes.length > 0) {
        //ofir code
        //if with undo
        if (noUndo) {
          for (const { id, patch } of changes) _patchAnything(id, patch);
        } else {
          executeMutation?.({
            do: () => {
              for (const { id, patch } of changes) _patchAnything(id, patch);
            },
            undo: () => {
              for (const { id, inversePatch } of changes) _patchAnything(id, inversePatch);
            },
          });
        }
      }
    },
    [syncService]
  );

  function _patchCanvasElements(mutations: Array<[string, ImmerPatch[], ImmerPatch[]]>) {
    const changes = mutations.map(([id, patch]) => ({ id, patch }));
    syncService?.mutate.patchCanvasEl({ changes });
  }
  const patchCanvasElements = (args: Array<[string, ImmerPatch[], ImmerPatch[]]>) =>
    executeMutation?.({
      do: () => _patchCanvasElements(args),
      undo: () => _patchCanvasElements(args.map(([id, patch, inversePatch]) => [id, inversePatch, patch])),
    });

  function executeSetElementsHidden(ids: string[], hidden: boolean) {
    const info = ids.reduce((map: Map<string, ElementDrawProps>, id: string) => {
      map.set(id, { hidden } as ElementDrawProps);
      return map;
    }, new Map());
    setSelectedElementsIds([]);
    mutateElementsProperties(info);
  }

  function executeReplaceElements(newIds: string[], oldIds: string[]) {
    const info = newIds.reduce((map: Map<string, ElementDrawProps>, id: string) => {
      map.set(id, { hidden: false } as ElementDrawProps);
      return map;
    }, new Map());
    for (const id of oldIds) {
      info.set(id, { hidden: true } as ElementDrawProps);
    }
    setSelectedElementsIds(newIds);
    mutateElementsProperties(info);
  }

  function executeChangeElement(id: string, props: Map<string, any>, previousProps: Map<string, any>) {
    const properties = [...props].map(([key, value]) => ({
      key,
      value,
    }));
    syncService?.mutate.changeElementProperties({ id, properties: properties });
    return () => executeChangeElement(id, previousProps, props);
  }

  function onDraw(id: string, point: Point) {
    if (showSubmenu) {
      setShowSubmenu(false);
    }
    syncService?.mutate.drawLine({ id, point });
  }

  // single element mutation
  function onDragElement(id: string, position: Point) {
    if (id) {
      syncService?.mutate.changeElementProperties({
        id,
        properties: [
          { key: "x", value: position.x },
          { key: "y", value: position.y },
        ],
      });
    }
  }

  // move multiple elements, by delta (not absolute position)
  function onMoveElements(ids: string[], delta: Point) {
    if (delta.x == 0 && delta.y == 0) return;
    patchElementsWithUndoRedo?.(ids, (draft) => {
      draft.x += delta.x;
      draft.y += delta.y;
    });
  }

  function onResizeElement(id: string, position: Point, scaleX: number, scaleY: number, rotation: number) {
    // if (Object.keys(selectedElementsId).length == 1) {
    syncService?.mutate.changeElementProperties({
      id,
      properties: [
        { key: "x", value: position.x },
        { key: "y", value: position.y },
        { key: "scaleX", value: scaleX },
        { key: "scaleY", value: scaleY },
        { key: "rotate", value: rotation },
      ],
    });
    //  }
  }

  // multiple elements mutation
  const mutateElementsProperties = (info: Map<string, any>) => {
    syncService?.mutate.changeElements({ info: [...info.entries()] });
  };

  const onElementsMutationEnded = useCallback(
    (
      info: Map<string, any>,
      undoInfo: Map<string, any>,
      updateSubMenuData?: SubmenuDataUpdate,
      addUndo: boolean = true
    ) => {
      if (addUndo) {
        executeMutation?.({
          do: () => mutateElementsProperties(info),
          undo: () => mutateElementsProperties(undoInfo),
        });
      } else {
        mutateElementsProperties(info);
      }
      function updateObjectRecurseInPlace(initial: any, update: any) {
        for (const key in update) {
          if (typeof initial[key] == "object" && typeof update[key] == "object") {
            initial[key] = updateObjectRecurseInPlace(initial[key], update[key]);
          } else {
            initial[key] = update[key];
          }
        }
        return initial;
      }
      if (updateSubMenuData) {
        setSubmenuData((current) => updateObjectRecurseInPlace({ ...current }, updateSubMenuData));
      }
    },
    [setSubmenuData]
    // [executeMutation?., setSubmenuData, mutateElementsProperties]
  );

  async function updateCursor({ pageX, pageY }: { pageX: number; pageY: number }) {
    if (
      isOnline &&
      collaboratorsCursorIds.length > 0 && // when no collaborators, no need to update cursor
      stageDivRef &&
      stageDivRef.current
    ) {
      const cursor = {
        x: (pageX - posScale.x) / (posScale.scale * 10),
        y: (pageY - posScale.y - stageDivRef.current.offsetTop) / (posScale.scale * 10),
      };

      if (showCursors && !isReadOnly) {
        syncService?.mutate.setCursor(cursor);
      }
    }
  }

  function updateClientViewport({ x, y, scale }: { x: number; y: number; scale: number }) {
    if (followingCollaboratorId) {
      tracking.trackEvent(consts.TRACKING_CATEGORY.COLLABORATION, "stop-following-collaborator");
    }
    setFollowingCollaboratorId(null);
    if (
      isOnline &&
      collaboratorsCursorIds.length > 0 && // when no collaborators, no need to update cursor
      stageDivRef &&
      stageDivRef.current &&
      !isReadOnly
    ) {
      syncService?.mutate.setClientViewport({ position: { x, y }, scale });
    }
  }

  function renderCursors() {
    if (!showCursors) {
      return null;
    }

    return (
      <>
        {collaboratorsCursorIds.map((id) => (
          <Collaborator key={id} syncService={syncService} clientID={id} position={posScale} scale={posScale.scale} />
        ))}
      </>
    );
  }

  function onUpgradeClick(source: string) {
    tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.UPGRADE_CTA);
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "upgrade-cta-click", source);
    setShowUpgradeModal({ name: "general", value: source });
  }

  function renderUpgradeCTA() {
    return <UpgradeCTA onClick={() => onUpgradeClick("upgrade-header-cta")} />;
  }

  function onInviteClick(title?: string) {
    if (isRestrictedUser) {
      setShowRestrictedModal(true);
      return;
    }
    const underScoredTitle = title ? `${title.toLowerCase().replace(/ /g, "_")}_` : "";
    const action = title === "Share" ? "invite_button_clicked" : `${underScoredTitle}invite_button_clicked`;
    const userPermissionString =
      board && canEditInstance(globalState.user, board, InstanceType.Board) ? "editor" : "viewer";
    const canInvite =
      board && canInviteToInstance(globalState.user, board, InstanceType.Board) ? "can-invite" : "cannot-invite";
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.ADMIN_ACTION,
      action,
      "invite_type",
      "canvas",
      userPermissionString,
      canInvite
    );
    setShowInviteModal(true);
  }

  function renderBoardUsersPresence() {
    return (
      <BoardUsersPresence
        clientIds={collaboratorsIds}
        syncService={syncService}
        followingClientId={followingCollaboratorId}
        onClientIdSelected={setFollow}
        isAnonymous={user?.isAnonymous}
      />
    );
  }

  const onClickVideoCall = useCallback(() => {
    let onClick;

    if ((user?.isAnonymous && isReadOnly) || isUserDisconnected) {
      return null;
    }

    // Guest users can't use video calls. They have to join/create an account first
    if (user?.isAnonymous) {
      onClick = () => setShowSignUpToContinueModal(true);
    } else if (isRestrictedUser) {
      onClick = () => setShowRestrictedModal(true);
    } else if (videoCallsFeature) {
      onClick = () => setVideoPanelOpen((x) => !x);
    } else {
      onClick = () => setShowUpgradeModal({ name: "video-call", value: "" });
    }

    return onClick();
  }, [
    isReadOnly,
    isRestrictedUser,
    isUserDisconnected,
    setShowRestrictedModal,
    setShowSignUpToContinueModal,
    setVideoPanelOpen,
    user?.isAnonymous,
    videoCallsFeature,
  ]);

  function renderVideoCallButton() {
    return (
      <VideoCallButton
        key="video-call-button"
        syncService={syncService}
        onClick={onClickVideoCall}
        isAnonymousUser={user?.isAnonymous ?? false}
      />
    );
  }

  function renderCommentButton() {
    if (!cordEnabled()) {
      return null;
    }

    return (
      <CommentButton
        loading={!enableComments}
        onClick={() => {
          if (!enableComments) return;

          // This function was migrated from sub-menu.tsx onItemClick.
          // It should behave identically to onItemClick, but only for the comment button.
          if (isRestrictedUser) {
            setShowRestrictedModal(true);
            return;
          }

          if (user?.isAnonymous) {
            setShowSignUpToContinueModal(true);
            return;
          }

          if (boardConfig.isViewOnly) {
            boardValueDispatch({ type: "show_signup", payload: true });
            return;
          }
          tracking.trackEvent(
            consts.TRACKING_CATEGORY.SIDE_MENU,
            "item clicked",
            CursorType.comment,
            board?.accountId,
            user?.id
          );
          setCursorType(CursorType.comment);
        }}
      />
    );
  }

  function renderDevToolsButton() {
    if (window.location.hostname === "localhost") {
      return <DevToolsButton key="devtools-button" onClick={() => setShowDevTools(!showDevTools)} />;
    }
    if (!initialShowDevTools) {
      return null;
    }
    return <DevToolsButton key="devtools-button" onClick={() => setShowDevTools(!showDevTools)} />;
  }

  function renderFPSMeterButton() {
    if (!initialShowDevTools) {
      return null;
    }
    return <FPSMeterButton key="fps-meter-button" onClick={() => setShowFpsMeter(!showFpsMeter)} />;
  }

  function renderKonvaPerformanceMetricsButton() {
    if (!initialShowDevTools) {
      return null;
    }
    return (
      <KonvaPerfButton
        key="konva-perf-button"
        onClick={() => setShowKonvaPerformanceMetrics(!showKonvaPerformanceMetrics)}
        isOn={showKonvaPerformanceMetrics}
      />
    );
  }

  const onPresentClicked = useCallback(() => {
    if (isCimReorderEnabled && (!visibleFrames || visibleFrames?.length === 0)) {
      setError("No frames to present");
      return;
    }
    if (isRestrictedUser) {
      setShowRestrictedModal(true);
    } else {
      const { pathname, query } = router;
      router.push({ pathname, query: { ...query, presentation: true } }, undefined, {
        shallow: true,
      });
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "present_button_clicked",
        null,
        board?.accountId,
        user?.id
      );
    }
  }, [isRestrictedUser, setShowRestrictedModal, router, board?.accountId, user?.id, visibleFrames]);

  function renderToggleSideMenu() {
    return (
      <ToggleSideMenu
        key="side-menu-button"
        isSideMenuOpen={showLeftSidePanel}
        onChange={(showSidePanel) => {
          if (isRestrictedUser) {
            setShowRestrictedModal(true);
          } else {
            setOpenedSidePanel(showSidePanel ? SidepanelType.framesAndComments : SidepanelType.none);
            setShowSideMenuOverflow(false);
          }
        }}
        onPresentClicked={onPresentClicked}
      />
    );
  }

  function renderExportButton() {
    if ((user?.isAnonymous && isReadOnly) || isUserDisconnected) {
      return null;
    }

    return (
      <ExportButton
        key="export-button"
        onClick={() => {
          if (isRestrictedUser) {
            setShowRestrictedModal(true);
          } else {
            setShowSideMenuOverflow(false);
            if (user?.isAnonymous) {
              setShowSignUpToContinueModal(true);
            } else {
              setShowExportModal(true);
              stageRef?.prepareForExport();
            }
          }
        }}
      />
    );
  }

  function renderVotingButton() {
    return (
      <VotingButton
        key="voting-button"
        highlight={votingPanelOpen}
        onClick={() => {
          if (isRestrictedUser) {
            setShowRestrictedModal(true);
          } else {
            handleSetVotingOpen(!votingPanelOpen);
          }
        }}
        disabled={false}
      />
    );
  }

  function renderTimerButton() {
    return (
      <Timer
        key="timer-button"
        relativeTo={timerButtonRef}
        isOnline={isOnline}
        syncService={syncService}
        setShowSideMenuOverflow={setShowSideMenuOverflow}
        isReadOnly={isReadOnly}
        onClick={isRestrictedUser ? () => setShowRestrictedModal(true) : undefined}
      />
    );
  }

  function renderUndoRedoControls() {
    return (
      <UndoRedoControls
        key="undo-redo-button"
        undoEnabled={canUndo}
        redoEnabled={canRedo}
        onUndo={executeUndo}
        onRedo={executeRedo}
        onClick={isRestrictedUser ? () => setShowRestrictedModal(true) : undefined}
      />
    );
  }

  function renderZoomControls() {
    return (
      <ZoomControls
        key="zoom-button"
        scale={posScale.scale}
        position={posScale}
        onZoom={(scale: number, position: Point) => {
          // TODO: callback should accept posScale type
          onChangePosScale({ scale, x: position.x, y: position.y });
        }}
        tipName={TipName.walkthroughExploreOrgChart}
        onTipActionClick={onOrgChartTipClick}
        stageRef={stageRefAtomValue ?? undefined}
      />
    );
  }

  function renderFooter() {
    if (isCimReorderEnabled) {
      return <AppFooter />;
    }

    const editorButtons = [
      renderToggleSideMenu,
      renderExportButton,
      renderVotingButton,
      renderTimerButton,
      renderVideoCallButton,
      renderCommentButton,
      renderDevToolsButton,
      renderFPSMeterButton,
      renderKonvaPerformanceMetricsButton,
    ];
    const viewerButtons = [renderExportButton, renderTimerButton];
    const restrictedButtons = editorButtons;

    const editorControllers = [renderUndoRedoControls, renderZoomControls];
    const restrictedControllers = editorControllers;
    const viewerControllers = [renderZoomControls];

    const buttonsToRender = () => {
      if (isRestrictedUser) {
        return restrictedButtons;
      } else if (canEditBoard) {
        return editorButtons;
      } else {
        return viewerButtons;
      }
    };

    const controllersToRender = () => {
      if (isRestrictedUser) {
        return restrictedControllers;
      } else if (canEditBoard) {
        return editorControllers;
      } else {
        return viewerControllers;
      }
    };
    return (
      <Footer>
        {buttonsToRender().map((button) => button())}
        <div className="spacer"></div>
        {controllersToRender().map((controller) => controller())}
        {shouldShowMondayIntegrationPopup.isOpen && <MondayIntegrationPopup />}
      </Footer>
    );
  }

  function onOrgChartTipClick() {
    setShowExploreOrgChart(true);
    tracking.trackEvent(consts.TRACKING_CATEGORY.WALKTHROUGH, "tip_video_opened", TipName.walkthroughExploreOrgChart);
  }

  //callbacks to be passed as props to memoed components
  const onChangeElementCallback = useCallback(onChangeElement, [submenuData]);
  const onResizeElementCallback = useCallback(onResizeElement, []);

  const onChooseIntegrationItems = useCallback(
    (items: any[], pos?: PointInViewport) => {
      const dummyIds = items.map(() => `${consts.CANVAS_ELEMENTS.INTEGRATION}-dummy-id`);
      if (!canAddElementsWithMondayLimitation(dummyIds)) {
        setShowUpgradeModal({ name: "monday-integration-limit", value: "monday-integration-limit" });
        return;
      }

      const calculateCurrentPosition = () => {
        const posScale = {
          ...(stageRef?.position() ?? { x: 0, y: 0 }),
          scale: stageRef?.scaleX() ?? 1,
        };
        return {
          x: ((pos as Point).x - posScale.x) / posScale.scale - 200,
          y: ((pos as Point).y - posScale.y) / posScale.scale - 70,
        };
      };

      const createElementData = (
        position: Point,
        { integrationId, integrationType, configuration }: any
      ): NewElementData => {
        if (newMondayTaskEnabled) {
          const element = placeMondayTask(position, { mondayItemId: configuration.itemId, integrationId });
          return {
            id: element.id,
            type: element.mondayTask.type,
            element: element.mondayTask as MondayTaskElement,
          };
        } else {
          const element = placeIntegrationElement(position, integrationId, integrationType, configuration);
          return {
            id: element.id,
            type: element.item.type,
            element: element.item as IntegrationItem,
          };
        }
      };

      let currentPosition = pos ? calculateCurrentPosition() : stageRef?.getCenterPosition() || { x: 0, y: 0 };
      const data: NewElementData[] = [];
      for (const item of items) {
        data.push(createElementData(currentPosition, item));
        currentPosition = { x: currentPosition.x + 100, y: currentPosition.y + 100 };
      }

      onAddElements(data, true, "integration-side-menu");
    },
    [canAddElementsWithMondayLimitation, onAddElements, stageRef, newMondayTaskEnabled]
  );

  const autoPlaceTemplate = useTemplateAutoPlacement();
  const isAutoPlacementEnabled = useFeatureFlag("templates-auto-placement");

  const onChooseTemplate = useCallback(
    async ({ template }: { template: CanvasTemplate }) => {
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.TEMPLATES,
        "canvas_template_selected_inside_canvas_templates",
        template.id
      );
      if (template.alias == "org-chart-integration") {
        setShowSubmenu(true);
        setCursorType(CursorType.mindmapOrgChart);
        setModalDeepLink({ modalType: RESET });
      } else if (isAutoPlacementEnabled) {
        // Auto-place template immediately (new behavior)
        setCursorType(CursorType.pointer);
        setShowSubmenu(false);
        setModalDeepLink({ modalType: RESET });

        try {
          await autoPlaceTemplate(template, "canvas-template-library", (status) => {
            setError(status);
          });
        } catch (error_) {
          console.error("Error auto-placing template:", error_);
          setError("Failed to place template");
          setTimeout(() => setError(null), 3000);
        }
      } else {
        // Use cursor-based placement (old behavior)
        setShowSubmenu(false);
        setSubmenuData({
          ...submenuData,
          template,
        });
        setCursorType(CursorType.template);
        setModalDeepLink({ modalType: RESET });
      }
    },
    [submenuData, autoPlaceTemplate, setError, setModalDeepLink, isAutoPlacementEnabled]
  );

  const onChooseShapeFromSidePanel = useCallback(
    (elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo) => {
      const { library, group, item } = elementInfo;
      tracking.trackAnalyticsEvent("shape_selected_from_library", {
        boardId: board?.id,
        source: "side-panel",
        library,
        group,
        item,
      });

      const newCursorType = getCursorTypeForElementType(elementType);
      setCursorType(newCursorType);
      setShowSubmenu(false);

      if (!submenuData[elementType as keyof SubmenuData]) return;
      setSubmenuData((previous) => ({
        ...previous,
        [elementType]: {
          ...previous[elementType as keyof SubmenuData],
          ...elementData,
          override: elementData?.override ?? {},
          libraryPath: `${library}:${group}:${item}`,
        },
      }));
    },
    [board?.id, submenuData]
  );

  const onChooseNounIcon = useCallback((icon: SubmenuData["nounIcon"]) => {
    setCursorType(CursorType.nounIcon);
    setSubmenuData((previous) => ({ ...previous, nounIcon: icon }));
  }, []);

  const onShapeFromSidePanelDragEnd = useCallback(() => {
    setCursorType(CursorType.pointer);
  }, []);

  async function isCanvasContainsIntegrationElements(syncService: SyncService<RW> | null) {
    const scanAndThrow = async (tx: ReadTransaction, prefix: string, additionalCheck?: (element: any) => boolean) => {
      for await (const [, element] of tx.scan({ prefix }).entries()) {
        if ((element as any).hidden) continue;
        if (additionalCheck && !additionalCheck(element)) continue;
        // Return when an integration is found
        return;
      }
      // Throw an error when no integration is found
      throw new Error("No integration found");
    };

    const rep = syncService?.getReplicache();
    if (!rep) {
      return false;
    }

    return await rep.query(async (tx: ReadTransaction) => {
      const canvasElementPrefix = "cElement-";
      const promises = [
        scanAndThrow(tx, canvasElementPrefix + consts.CANVAS_ELEMENTS.INTEGRATION),
        scanAndThrow(tx, canvasElementPrefix + consts.CANVAS_ELEMENTS.MONDAY_TASK),
        scanAndThrow(tx, canvasElementPrefix + consts.CANVAS_ELEMENTS.LIVE_INTEGRATION),
        scanAndThrow(tx, canvasElementPrefix + consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART),
        scanAndThrow(
          tx,
          canvasElementPrefix + consts.CANVAS_ELEMENTS.TIMELINE,
          (element) => !!(element as any).integrationId
        ),
      ];

      // resolves when any promise resolves (integration found) or reject when all promises reject (no integration found)
      return Promise.any(promises)
        .then(() => true)
        .catch(() => false);
    });
  }

  const onExport = useCallback(
    (withWatermark: boolean, config: ExportConfiguration): void => {
      if (stageRef) {
        stageRef.exportStage(config, withWatermark, posScale, posScale.scale).finally(() => {
          setShowExportModal(false);
        });
      } else {
        setError("Export failed. We're sorry about this inconvencience. Please try again later.");
        console.error("failed to export canvas because can't find stage ref");
        setShowExportModal(false);
      }
    },
    [stageRef, posScale, setShowExportModal, setError]
  );

  const onUpgrade = useCallback(() => {
    tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.UPGRADE_CTA);
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "upgrade-cta-click", "remove-watermark-button");
    setShowUpgradeModal({ name: "general", value: "" });
    setShowExportModal(false);
  }, [setShowUpgradeModal, setShowExportModal]);

  return (
    <CordProvider clientAuthToken={cordAuth} enableTasks={false} cordScriptUrl={process.env.CORD_SDK_URL}>
      <FooterProvider
        onFocusElement={(id: string) => stageRef?.focusElement(id)}
        onFrameDeleted={(id: string) => onDeleteElements([id])}
        onChangePosScale={(posScale: { scale: number; x: number; y: number }) => {
          onChangePosScale(posScale);
        }}
        onOrgChartTipClick={onOrgChartTipClick}
        onCursorTypeChange={setCursorType}
        onChooseIntegrationItems={onChooseIntegrationItems}
      >
        <HeaderProvider
          onExport={onExport}
          onUpgrade={onUpgrade}
          setCreatingTemplate={() => setCreatingTemplate(true)}
          onPresentClicked={onPresentClicked}
          setVotingOpen={handleSetVotingOpen}
          onClickVideoCall={onClickVideoCall}
          onClickVideoCallHangUp={closeVideoPanelCallback}
          setCursorType={setCursorType}
        >
          <DndProvider backend={HTML5Backend} context={window}>
            <MondayItemManagerProvider documentId={board!.documentId} pusherChannel={pusherChannel}>
              <AppHeader
                reps={reps}
                isBoardHeader={true}
                showsSwitcher={false}
                title={
                  !isCIM() && board ? (
                    <BoardTitle board={board} isReadOnly={isReadOnly} isAnonymous={!!user?.isAnonymous} />
                  ) : null
                }
                canEditBoard={canEditBoard}
                renderUpgradeCTA={renderUpgradeCTA}
                renderBoardUsersPresence={renderBoardUsersPresence}
                onInviteClick={onInviteClick}
                onCreateTemplate={() => setCreatingTemplate(true)}
                onCreateTemplateLink={() => setShowTemplateLinkModal(true)}
                setShowUpgradeModal={setShowUpgradeModal}
                onRestrictedClick={() => setShowRestrictedModal(true)}
                setFollow={setFollow}
              ></AppHeader>
              <div id="pick-files"></div>
              <div
                id="stage-container"
                ref={stageDivRef}
                data-cursor={cursorType}
                className={style.designer}
                style={{ overflow: isIframe ? "hidden" : undefined }}
                onMouseMove={updateCursor}
                onContextMenu={(e) => {
                  if ((e.target as any)?.nodeName == "CANVAS") {
                    e.preventDefault();
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <CanvasStage
                  cursorType={isReadOnly ? CursorType.move : cursorType}
                  setCursorType={setCursorType}
                  posScale={posScale}
                  onChangePosScale={onChangePosScale}
                  onDragElement={onDragElement}
                  onMoveElements={onMoveElements}
                  onResizeElement={onResizeElementCallback}
                  onChangeElement={onChangeElementCallback}
                  patchCanvasElement={patchCanvasElement}
                  patchCanvasElements={patchCanvasElements}
                  patchAnything={patchAnything}
                  onAddElements={onAddElements}
                  onDeleteElements={onDeleteElements}
                  onReplaceElements={onReplaceElements}
                  onDraw={onDraw}
                  onElementsSelected={setSelectedElementsIds}
                  onElementsMutationEnded={onElementsMutationEnded}
                  selectedElementIds={selectedElementsId}
                  setSelectedElementsIds={setSelectedElementsIds}
                  syncService={syncService}
                  onUndo={executeUndo}
                  onRedo={executeRedo}
                  onError={setError}
                  showUpgradeModal={(reason: CheckoutSources) => setShowUpgradeModal({ name: reason, value: reason })}
                  cordAuthToken={cordAuth}
                  setShowSubmenu={setShowSubmenu}
                  setShowSideMenuOverflow={setShowSideMenuOverflow}
                  isReadOnly={isReadOnly}
                  onMouseDown={() => setFollowingCollaboratorId(null)}
                  undoRedoStack={undoRedoStack}
                  showKonvaPerformanceMetrics={showKonvaPerformanceMetrics}
                  needToRefreshBoundsRef={needToRefreshBoundsRef}
                  customColors={customColors}
                />
                {openedSidePanel == SidepanelType.aiPanel && (
                  <AIGeneratorPanel onClose={() => setOpenedSidePanel(SidepanelType.none)} />
                )}
              </div>
              {isCIM() && isCimReorderEnabled && <CIMOnboardingFlow />}
              {isCIM() && isCimReorderEnabled && <CIMContentCards />}

              <div className={style.cursors}>{renderCursors()}</div>
              <div className={style.controls}>
                {isCimReorderEnabled ? (
                  <div style={{ pointerEvents: "visible" }}>
                    {showIntegrationsSidePanel && (
                      <MondayIntegrationSidePanel
                        documentId={board?.documentId}
                        onChooseItems={onChooseIntegrationItems}
                        pusherChannel={pusherChannel}
                        onClose={() => setOpenedSidePanel(SidepanelType.none)}
                        syncService={syncService}
                      />
                    )}
                    {showShapesSidePanel && (
                      <ShapesSidePanel
                        cursorType={cursorType}
                        onMenuItemClick={onChooseShapeFromSidePanel}
                        onUpgradeRequired={
                          isShapePanelUnlocked ? undefined : () => showBlockedActionModal("premium-shapes")
                        }
                        onMenuItemDragEnd={onShapeFromSidePanelDragEnd}
                      />
                    )}

                    {showOrgChartNodeInfo && <MemoedCanvasMindmapOrgChartSidePanel />}
                    {openedSidePanel == SidepanelType.orgChartNodeInfo && (
                      <OrgChartSidePanel
                        syncService={syncService}
                        patchCanvasElement={patchCanvasElement}
                        onClose={() => setOpenedSidePanel(SidepanelType.none)}
                        onUpgradeRequired={
                          canAddOrgChartFields ? undefined : () => showBlockedActionModal("org-chart-fields")
                        }
                      />
                    )}
                  </div>
                ) : (
                  <div
                    className={classNames(style.sidePanel, {
                      [style.hideSidePanel]: isSidePanelHidden,
                      [style.cimSidePanel]:
                        isCimReorderEnabled &&
                        (showIntegrationsSidePanel ||
                          showShapesSidePanel ||
                          openedSidePanel == SidepanelType.orgChartNodeInfo ||
                          openedSidePanel == SidepanelType.timer ||
                          openedSidePanel == SidepanelType.comments ||
                          openedSidePanel == SidepanelType.frames ||
                          openedSidePanel == SidepanelType.voting ||
                          openedSidePanel == SidepanelType.userView ||
                          openedSidePanel == SidepanelType.aiPanel),
                    })}
                  >
                    {showLeftSidePanel && (
                      <MemoedCanvasSidePanel
                        syncService={syncService}
                        onFocusElement={(id: string) => stageRef?.focusElement(id)}
                        onDeleteElement={(id: string) => onDeleteElements([id])}
                        cordAuthToken={cordAuth}
                        changeCommentStatus={(id, open) => {
                          const info = new Map();
                          const fullid = dbKey(id, consts.CANVAS_ELEMENTS.COMMENT);
                          info.set(fullid, { hidden: !open });
                          mutateElementsProperties(info);
                        }}
                        onCursorTypeChange={setCursorType}
                        onClose={() => setOpenedSidePanel(SidepanelType.none)}
                      />
                    )}
                    {showIntegrationsSidePanel && (
                      <MondayItemManagerProvider documentId={board!.documentId}>
                        <MondayIntegrationSidePanel
                          documentId={board?.documentId}
                          onChooseItems={onChooseIntegrationItems}
                          pusherChannel={pusherChannel}
                          onClose={() => setOpenedSidePanel(SidepanelType.none)}
                          syncService={syncService}
                        />
                      </MondayItemManagerProvider>
                    )}
                    {showShapesSidePanel && (
                      <ShapesSidePanel
                        cursorType={cursorType}
                        onMenuItemClick={onChooseShapeFromSidePanel}
                        onUpgradeRequired={
                          isShapePanelUnlocked ? undefined : () => showBlockedActionModal("premium-shapes")
                        }
                        onMenuItemDragEnd={onShapeFromSidePanelDragEnd}
                      />
                    )}
                    {showOrgChartNodeInfo && <MemoedCanvasMindmapOrgChartSidePanel />}
                    {openedSidePanel == SidepanelType.orgChartNodeInfo && (
                      <OrgChartSidePanel
                        syncService={syncService}
                        patchCanvasElement={patchCanvasElement}
                        onClose={() => setOpenedSidePanel(SidepanelType.none)}
                        onUpgradeRequired={
                          canAddOrgChartFields ? undefined : () => showBlockedActionModal("org-chart-fields")
                        }
                      />
                    )}
                  </div>
                )}
                <div
                  id="controls-area"
                  className={classNames(
                    style.canvasControls,
                    isBannerNotificationOn && style.withBannerNotificationOn,
                    isCimReorderEnabled && style.cimCanvasControls
                  )}
                >
                  <div
                    id="no-controls-area"
                    className={classNames(style.noControlsArea, isCimReorderEnabled && style.cimNoControlsArea)}
                    data-testid="no-controls-area"
                  />
                  <FollowingCollaboratorContainer collaborator={followingCollaborator} isCIM={isCimReorderEnabled} />
                  {(canEditBoard || isRestrictedUser) && (
                    <div className={classNames(style.sidemenu, isCimReorderEnabled && style.cimSideMenu)}>
                      <CanvasSideMenu
                        currentCursorType={cursorType}
                        onChangeCursorType={(item: CursorType) => {
                          tracking.trackEvent(
                            consts.TRACKING_CATEGORY.SIDE_MENU,
                            "item clicked",
                            item,
                            board?.accountId,
                            user?.id
                          );
                          submenuItemClicked(item);
                        }}
                        setShowSidePanel={(show) =>
                          setOpenedSidePanel(show ? SidepanelType.framesAndComments : SidepanelType.none)
                        }
                        toggleShowIntegrations={() => {
                          tracking.trackEvent(
                            consts.TRACKING_CATEGORY.INTEGRATIONS,
                            "monday_integration_button_clicked",
                            null,
                            board?.accountId,
                            user?.id
                          );
                          setOpenedSidePanel((type) =>
                            type === SidepanelType.integrations ? SidepanelType.none : SidepanelType.integrations
                          );
                        }}
                        showSubmenu={showSubmenu}
                        setShowSubmenu={setShowSubmenu}
                        setCursorType={(c: CursorType) => {
                          tracking.trackEvent(
                            consts.TRACKING_CATEGORY.SIDE_MENU,
                            "item clicked",
                            c,
                            board?.accountId,
                            user?.id
                          );
                          setCursorType(c);
                        }}
                        showSideMenuOverflow={showSideMenuOverflow}
                        setShowSideMenuOverflow={setShowSideMenuOverflow}
                        setShowRestrictedModal={setShowRestrictedModal}
                        isCimReorderEnabled={isCimReorderEnabled}
                      />
                    </div>
                  )}
                  {(canEditBoard || isRestrictedUser) && (
                    <NounIconPanelContainer
                      onChooseIcon={onChooseNounIcon}
                      onDismiss={() => setCursorType(CursorType.pointer)}
                    />
                  )}

                  {renderFooter()}
                  {showDevTools && (
                    <CanvasElementsSchemaViewer syncService={syncService} onClose={() => setShowDevTools(false)} />
                  )}
                  {error && <ErrorToast text={error} onClose={() => setError(null)} />}
                  {isLoading && (
                    <Modal dimBackground={true}>
                      <AppLoader />
                    </Modal>
                  )}
                  {showExploreOrgChart && (
                    <Modal dimBackground={true} onClickBackground={() => setShowExploreOrgChart(false)}>
                      <VideoPlayerModal
                        onDismiss={() => setShowExploreOrgChart(false)}
                        handleVideoEnded={() =>
                          tracking.trackEvent(
                            consts.TRACKING_CATEGORY.WALKTHROUGH,
                            "tip_video_ended",
                            TipName.walkthroughExploreOrgChart
                          )
                        }
                        videoId={walkthroughTips[TipName.walkthroughExploreOrgChart].video}
                      />
                    </Modal>
                  )}
                  {isPresentation && (
                    <Modal dimBackground={true}>
                      <CanvasPresentationView
                        syncService={syncService}
                        onDismiss={() => {
                          const { pathname, query } = router;
                          delete query["presentation"];
                          router.push({ pathname, query }, undefined, { shallow: true });
                        }}
                      />
                    </Modal>
                  )}
                  <VotingSessionCountdown syncService={syncService} />
                </div>

                {!isReadOnly &&
                  (isCimReorderEnabled && votingPanelOpen ? (
                    <VotingMainPanel syncService={syncService} onDismiss={() => setVotingPanelOpen(false)} />
                  ) : (
                    votingPanelOpen && (
                      <div className={style.sidePanel}>
                        <VotingMainPanel syncService={syncService} onDismiss={() => setVotingPanelOpen(false)} />
                      </div>
                    )
                  ))}
                <VotingUploader setShowUploadingVotesModal={setShowUploadingVotesModal} />
                {showUploadingVotesModal && <UploadInProgress />}
                {showUpgradeModal && (
                  <Checkout
                    user={globalState.user!}
                    onDismiss={() => {
                      setShowUpgradeModal(null);
                    }}
                    source={showUpgradeModal}
                  />
                )}
                <SignUpToContinue />
                {isCreatingTemplate && (
                  <Modal dimBackground={true}>
                    <CreateTemplateModal
                      onDismiss={() => setCreatingTemplate(false)}
                      syncService={syncService}
                      isCanvasContainsIntegrationElements={isCanvasContainsIntegrationElements}
                    />
                  </Modal>
                )}
                {showTemplateLinkModal && (
                  <Modal dimBackground={true}>
                    <TemplateLinkModal
                      onDismiss={() => setShowTemplateLinkModal(false)}
                      syncService={syncService}
                      isCanvasContainsIntegrationElements={isCanvasContainsIntegrationElements}
                    />
                  </Modal>
                )}
              </div>
              <div id={PortalID.Floaters} style={{ position: "fixed", zIndex: 1000 }} />
              <div id={PortalID.Tooltips} style={{ position: "fixed", zIndex: 1002 }} />
              <div id={PortalID.UiWidgets} style={{ position: "fixed", zIndex: 1003 }} />
              <div id={PortalID.Comments} />
              {videoPanelOpen && (
                <VideoCallUi syncService={syncService} onFail={setErrorCallback} onClose={closeVideoPanelCallback} />
              )}
              {htmlDisplay == HtmlDialogDisplay.ShowUpgradeModal && (
                <Checkout
                  user={globalState.user!}
                  onDismiss={() => {
                    setHtmlDisplay(HtmlDialogDisplay.None);
                  }}
                />
              )}
              {htmlDisplay == HtmlDialogDisplay.EditSeats && (
                <Modal dimBackground={true}>
                  <EditSeatsModal onDismiss={() => setHtmlDisplay(HtmlDialogDisplay.None)} />
                </Modal>
              )}
              {showExportModal && user && (
                <ExportDialog
                  getFramesFn={stageRef?.getVisibleFramesByOrder}
                  close={() => setShowExportModal(false)}
                  onExport={onExport}
                  // no monday user needs upgrade, can get rid of this
                  onUpgrade={onUpgrade}
                />
              )}
              {showInviteModal && (
                <Modal
                  dimBackground={true}
                  onClickBackground={() => setShowInviteModal(false)}
                  onDismissOnEscape={() => setShowInviteModal(false)}
                >
                  {board && (
                    <ShareCanvasModalNew
                      board={board}
                      setShowInviteModal={setShowInviteModal}
                      setShowUpgradeModal={setShowUpgradeModal}
                      passwordChecked={passwordChecked}
                      setPasswordChecked={setPasswordChecked}
                    />
                  )}
                </Modal>
              )}
              {showRestrictedModal && (
                <RestrictedUserModalNew trackingEvent={"canvas"} onDismiss={() => setShowRestrictedModal(false)} />
              )}
              {showRestrictedNoticedModal && (
                <RestrictedUserNoticeModalNew
                  trackingEvent={"canvas"}
                  onDismiss={() => {
                    markTipSeen(TipName.restrictedUsersNotice);
                    setShowRestrictedNoticeModal(false);
                  }}
                />
              )}
              <ModalManager
                setIsLoading={setIsLoading}
                handleModalAction={handleModalAction}
                onShowUpgradeModal={(value) => {
                  setModalDeepLink({ modalType: RESET });
                  value && setShowUpgradeModal({ name: "general", value });
                }}
                currentCanvasDocumentId={board?.documentId}
                onCreateTemplate={() => setCreatingTemplate(true)}
              />
            </MondayItemManagerProvider>
          </DndProvider>
        </HeaderProvider>
      </FooterProvider>
    </CordProvider>
  );
}

const MemoedCanvasSidePanel = React.memo(CanvasSidePanel);
const MemoedCanvasMindmapOrgChartSidePanel = React.memo(CanvasMindmapOrgChartSidePanel);
